import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import CommonImage from '@components/CommonImage';
import TeamMember from '@components/TeamMember';

import useDebounce from '@hooks/useDebounce';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { addMemberToFavourite, removeMemberFromFavourite } from '@services/favouriteService';

import ApiClient from '@lib/apiClient';

import { printApiError } from '@utils/Util';

import { toastSuccess } from '@/components/Toast';

const PRO_PIC_DEFAULT = '/images/pro_pic_default.svg';

const AddMemberToFavourite = ({ folderId, memberList }) => {
	const [usersByTypeList, setUsersByTypeList] = useState([]);
	const [addedMemberList, setAddedMemberList] = useState([]);
	const [showAddMemberModal, setShowAddMemberModal] = useState(false);
	const [searchUserSuggestions, setSearchUserSuggestions] = useState([]);
	const [searchUserText, setSearchUserText] = useState('');
	const dispatch = useDispatch();
	const AddNewMemberButton = useRef(null);
	const AddNewMemberModal = useRef(null);
	const MAX_MEMBERS = 4;

	const debouncedSearchUserText = useDebounce(searchUserText, 300);

	useEffect(() => {
		setAddedMemberList(memberList);
	}, [folderId, memberList?.length]);

	useEffect(() => {
		document.addEventListener('click', handleClickOutside, { capture: true });
		return () => {
			document.removeEventListener('click', handleClickOutside);
		};
	}, []);

	const handleClickOutside = (event) => {
		const clickedOutsideModal =
			AddNewMemberModal.current && !AddNewMemberModal.current.contains(event.target);

		const clickedOutsideButton =
			AddNewMemberButton.current && !AddNewMemberButton.current.contains(event.target);

		if (clickedOutsideModal && clickedOutsideButton) {
			setShowAddMemberModal(false);
		}
	};

	const getUsersByTypes = async () => {
		try {
			dispatch(openLoader());
			let params = '?userTypes=PROJECT_MANAGER&userTypes=FASHION_DESIGNER';
			const { data } = await ApiClient.GET('getUsersByTypes', params);
			setUsersByTypeList(data);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		getUsersByTypes();
	}, []);

	const MEMBER_DATA = {
		usersByTypeList,
		searchUserText,
		searchUserSuggestions,
		memberList: addedMemberList
	};

	const getSearchSuggestions = async (userInput) => {
		try {
			dispatch(openLoader());
			let params = `?email=${userInput}`;
			const { data } = await ApiClient.GET('getUserSuggestions', params);
			if (data) {
				setSearchUserSuggestions(data);
			}
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const onChangeTeam = (e) => {
		setSearchUserText(e.target.value);
	};

	useEffect(() => {
		if (debouncedSearchUserText.trim()) {
			getSearchSuggestions(debouncedSearchUserText);
		}
	}, [debouncedSearchUserText]);

	const addUserToFavourite = async (user) => {
		try {
			const isDuplicate = addedMemberList.some((member) => parseInt(member.id) === parseInt(user.id));
			if (isDuplicate) {
				return;
			}
			dispatch(openLoader());
			await addMemberToFavourite(folderId, user?.id);
			setAddedMemberList([...addedMemberList, user]);
			toastSuccess(`${user.name} added to this folder`);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const removeUserFromFavourite = async (user) => {
		try {
			dispatch(openLoader());
			await removeMemberFromFavourite(folderId, user?.id);
			setAddedMemberList(addedMemberList.filter((item) => item.id !== user.id));
			toastSuccess(`${user.name} removed from this folder`);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const renderIcon = (user) => {
		if (user.profilePicDocument && user.profilePicDocument.docUrl) {
			return (
				<CommonImage
					key={1}
					onClick={() => setShowAddMemberModal(!showAddMemberModal)}
					src={user.profilePicDocument.docUrl}
					alt=""
					data-toggle="tooltip"
					data-placement="top"
					title={user.name}
					isStatic={false}
				/>
			);
		}
		return (
			<CommonImage
				key={2}
				onClick={() => setShowAddMemberModal(!showAddMemberModal)}
				src={PRO_PIC_DEFAULT}
				alt=""
				data-toggle="tooltip"
				data-placement="top"
				title={user.name}
			/>
		);
	};

	const renderMemberIcon = (userResponseList) => {
		if (userResponseList.length > MAX_MEMBERS) {
			return userResponseList.slice(0, MAX_MEMBERS).map((user) => {
				return renderIcon(user);
			});
		} else {
			return userResponseList.map((user) => {
				return renderIcon(user);
			});
		}
	};

	return (
		<div className="position-relative">
			<div className="px-0 add-buyer d-flex justify-content-end">
				<div className="position-relative">
					<div className="added-members">
						<div
							id="AddNewMember"
							ref={AddNewMemberButton}
							className={`add-new-member ${showAddMemberModal ? 'show' : ''}`}
						>
							<CommonImage
								src="/icons/add-people.svg"
								alt="add"
								onClick={() => setShowAddMemberModal(!showAddMemberModal)}
							/>
						</div>

						{addedMemberList?.length > 0 && (
							<>
								{renderMemberIcon(addedMemberList)}
								{addedMemberList.length > MAX_MEMBERS && (
									<div className="more-people">
										<a
											href="#"
											onClick={() => setShowAddMemberModal(!showAddMemberModal)}
										>
											+{addedMemberList.length - MAX_MEMBERS}
										</a>
									</div>
								)}
							</>
						)}
					</div>
					<div
						className={`add-people-popup no-padding-popup shadow ${
							showAddMemberModal ? 'show' : ''
						}`}
						ref={AddNewMemberModal}
					>
						<div className="close-add-people mb-3 d-block d-sm-none">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="20.941"
								height="20.941"
								viewBox="0 0 20.941 20.941"
							>
								<g
									id="Group_22803"
									data-name="Group 22803"
									transform="translate(2489.29 -478.941)"
								>
									<line
										id="Line_153"
										data-name="Line 153"
										x2="25.615"
										transform="translate(-2487.875 480.355) rotate(45)"
										fill="none"
										stroke="#21242b"
										strokeLinecap="round"
										strokeWidth="2"
									/>
									<line
										id="Line_154"
										data-name="Line 154"
										x2="25.615"
										transform="translate(-2469.763 480.355) rotate(135)"
										fill="none"
										stroke="#21242b"
										strokeLinecap="round"
										strokeWidth="2"
									/>
								</g>
							</svg>
						</div>
						<TeamMember
							memberData={MEMBER_DATA}
							onAddMember={addUserToFavourite}
							onRemoveMember={removeUserFromFavourite}
							onChange={onChangeTeam}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default AddMemberToFavourite;
